// @givmo/crm — one segment, new or saved (CRM-5; the approved segment screen).
//
// The name, the conditions every member must meet, and beside them how many people match right now
// with the first few by name, and "See all". The count is the server's: it never includes a contact
// the organization may not email, and never a Givmo donor while the consent gate is closed (the card
// then carries the server's own sentence). Nothing here is sent anywhere: a segment is a saved filter
// over the CRM's contacts.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  DatePicker,
  Dialog,
  EmptyState,
  ErrorMessage,
  Field,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  testIdProp,
  toast,
  type SelectOption,
} from '@givmo/design-system';
import { Plus, ShieldAlert, Users, X } from 'lucide-react';
import { isGivmoError } from '@givmo/api-client';
import type { CrmModuleConfig } from './crm-config';
import {
  contactLastGift,
  displayDate,
  displayGroupedCount,
  displayText,
  isSliceUnavailable,
  serverMessage,
  type CrmSegmentDetailView,
} from './crm-decode';
import {
  crmKeys,
  crmSegmentEvaluationOptions,
  crmSegmentMembersOptions,
  crmSegmentDetailOptions,
  crmSegmentConditionOptions,
} from './crm-queries';
import { createCrmSegment, deleteCrmSegment, updateCrmSegment } from './crm-transport';
import { ContactsPagination, ContactsTable } from './contacts-table';
import {
  EMAIL_MISSING,
  EMAIL_ON_FILE,
  SOURCE_FEED,
  SOURCE_SUPPLIED,
  calendarDay,
  calendarDayString,
  definitionFromDrafts,
  draftKey,
  draftsComplete,
  draftsFromDefinition,
  isDateField,
  relationshipLabel,
  type ConditionDraft,
  type ConditionField,
  type ConditionOp,
} from './segment-conditions';
import { useDebouncedValue } from './use-debounced-value';
import type { CrmContactOut, CrmSegmentDefinition } from './wire-types';

const PREFIX = 'crm-segment';
/** The members the count card shows by name. */
const SAMPLE_SIZE = 5;
/** Rows per page in "See all". */
const MEMBERS_PAGE_SIZE = 10;

export function CrmSegmentBuilderPage({
  config,
  segmentId,
}: {
  config: CrmModuleConfig;
  segmentId?: string;
}) {
  if (segmentId === undefined) return <SegmentEditor config={config} saved={null} />;
  return <SavedSegment config={config} segmentId={segmentId} />;
}

function SavedSegment({ config, segmentId }: { config: CrmModuleConfig; segmentId: string }) {
  const segmentQuery = useQuery(crmSegmentDetailOptions(config.orgRef, segmentId));
  if (segmentQuery.isPending) {
    return (
      <PageFrame>
        <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-loading`)}>
          <Skeleton className="h-16 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </PageFrame>
    );
  }
  if (segmentQuery.isError && isSliceUnavailable(segmentQuery.error)) {
    return (
      <PageFrame>
        <EmptyState
          testId={`${PREFIX}-unavailable`}
          icon={<Icon icon={Users} size={20} aria-hidden />}
          title="Not available yet"
          description="This part of the portal isn’t switched on for your organization yet."
        />
      </PageFrame>
    );
  }
  if (segmentQuery.isError || segmentQuery.data === null) {
    // The server's one refusal for a segment that is not this organization's, or is gone: a 400
    // whose words only repeat the title, so the page says its own line. Any other failure keeps
    // the server's words.
    const gone = 'It may have been deleted.';
    return (
      <PageFrame>
        <EmptyState
          testId={`${PREFIX}-missing`}
          icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
          title="This segment isn’t here"
          description={
            isGivmoError(segmentQuery.error) && segmentQuery.error.status !== 400
              ? serverMessage(segmentQuery.error, gone)
              : gone
          }
        />
      </PageFrame>
    );
  }
  return (
    <SegmentEditor key={segmentQuery.data.segment.id} config={config} saved={segmentQuery.data} />
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-segment')}
    >
      {children}
    </div>
  );
}

/** The last gift date's field, in the class profile's word for the last gift ("Last gift date"). */
function lastGiftFieldLabel(config: CrmModuleConfig): string {
  return `${config.vocab.lastActivityLabel} date`;
}

/**
 * The years a last gift date's calendar lists, so a date years back is two choices away: from 1950,
 * about as far back as an organization's records plausibly reach, through this year.
 */
function lastGiftYears(): { startMonth: Date; endMonth: Date } {
  return { startMonth: new Date(1950, 0, 1), endMonth: new Date(new Date().getFullYear(), 11, 1) };
}

/** The older rule's field, kept on a saved row that has it: the imported dates alone. */
function importedLastGiftFieldLabel(config: CrmModuleConfig): string {
  return `${lastGiftFieldLabel(config)} (imports only)`;
}

/** A row's fields: the five, and the imported-only rule for the row that already has it. */
function fieldOptions(config: CrmModuleConfig, current: ConditionField): SelectOption[] {
  return [
    { value: 'tag', label: 'Tag' },
    { value: 'source', label: 'Source' },
    { value: 'relationship', label: 'Relationship' },
    { value: 'has_email', label: 'Email address' },
    { value: 'last_gift_date', label: lastGiftFieldLabel(config) },
    ...(current === 'imported_last_gift_date'
      ? [{ value: 'imported_last_gift_date', label: importedLastGiftFieldLabel(config) }]
      : []),
  ];
}

const WINDOW_UNITS: SelectOption[] = [
  { value: 'days', label: 'days' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' },
];

function opOptions(field: ConditionField): SelectOption[] {
  switch (field) {
    case 'has_email':
      return [{ value: 'is', label: 'is' }];
    case 'last_gift_date':
      return [
        { value: 'before', label: 'is before' },
        { value: 'after', label: 'is after' },
        { value: 'between', label: 'is between' },
        { value: 'within', label: 'is in the last' },
      ];
    case 'imported_last_gift_date':
      return [
        { value: 'before', label: 'is before' },
        { value: 'after', label: 'is after' },
        { value: 'between', label: 'is between' },
      ];
    default:
      return [
        { value: 'is', label: 'is' },
        { value: 'is_not', label: 'is not' },
      ];
  }
}

function withCurrent(options: SelectOption[], current: string, label: string): SelectOption[] {
  return current.length === 0 || options.some((option) => option.value === current)
    ? options
    : [...options, { value: current, label }];
}

function SegmentEditor({
  config,
  saved,
}: {
  config: CrmModuleConfig;
  saved: CrmSegmentDetailView | null;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const optionsQuery = useQuery(crmSegmentConditionOptions(config.orgRef));
  // A saved definition this screen cannot write back exactly is kept untouched: rename only.
  const [savedDrafts] = useState(() =>
    saved === null ? [] : draftsFromDefinition(saved.segment.definition),
  );
  const editable = savedDrafts !== null;
  const [name, setName] = useState(saved?.segment.name ?? '');
  const [drafts, setDrafts] = useState<ConditionDraft[]>(savedDrafts ?? []);
  const [seeded, setSeeded] = useState(saved !== null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const tags = optionsQuery.data?.tags ?? [];
  const relationshipTypes = optionsQuery.data?.relationship_types ?? [];

  // A new segment opens with one condition: the tag most contacts carry, when there is one.
  useEffect(() => {
    if (seeded || !optionsQuery.isSuccess) return;
    setSeeded(true);
    const first = optionsQuery.data.tags[0];
    setDrafts([
      first === undefined
        ? { key: draftKey(), field: 'source', op: 'is', value: SOURCE_SUPPLIED }
        : { key: draftKey(), field: 'tag', op: 'is', value: first.label },
    ]);
  }, [seeded, optionsQuery.isSuccess, optionsQuery.data]);

  const definitionJson = editable ? JSON.stringify(definitionFromDrafts(drafts)) : null;
  const debouncedJson = useDebouncedValue(definitionJson, 300);
  const definition = useMemo(
    () => (debouncedJson === null ? null : (JSON.parse(debouncedJson) as CrmSegmentDefinition)),
    [debouncedJson],
  );
  const evaluation = useQuery({
    ...crmSegmentEvaluationOptions(config.orgRef, definition ?? { v: 1, all: [] }, {
      limit: SAMPLE_SIZE,
      offset: 0,
    }),
    // Count only what the rows say once the typing has settled — and a new segment only once its
    // first condition is in, never a flash of everyone before it.
    enabled: definition !== null && seeded && debouncedJson === definitionJson,
  });

  // An editable segment counts what the screen shows; a kept one, what the server stored.
  const count = editable ? evaluation.data?.total : (saved?.segment.count ?? undefined);
  const sample = editable ? (evaluation.data?.members ?? []) : (saved?.sample ?? []);
  const closedMessage = editable
    ? evaluation.data?.consent_available === false
      ? evaluation.data.message
      : null
    : saved?.consent_available === false
      ? saved.message
      : null;

  const valueOptions = (draft: ConditionDraft): SelectOption[] => {
    switch (draft.field) {
      case 'tag':
        return withCurrent(
          tags.map((tag) => ({ value: tag.label, label: tag.label })),
          draft.value,
          draft.value,
        );
      case 'source':
        return [
          { value: SOURCE_FEED, label: config.vocab.feedSourceLabel },
          { value: SOURCE_SUPPLIED, label: config.vocab.suppliedSourceLabel },
        ];
      case 'relationship':
        return withCurrent(
          relationshipTypes.map((slug) => ({ value: slug, label: relationshipLabel(slug) })),
          draft.value,
          relationshipLabel(draft.value),
        );
      case 'has_email':
        return [
          { value: EMAIL_ON_FILE, label: 'On file' },
          { value: EMAIL_MISSING, label: 'Missing' },
        ];
      case 'last_gift_date':
      case 'imported_last_gift_date':
        // A date, picked on a calendar: no list to offer.
        return [];
    }
  };

  const firstValue = (field: ConditionField): string => {
    const options = valueOptions({ key: '', field, op: 'is', value: '' });
    return options[0]?.value ?? '';
  };

  const update = (key: string, change: Partial<ConditionDraft>) =>
    setDrafts((rows) => rows.map((row) => (row.key === key ? { ...row, ...change } : row)));

  // A new row offers the next tag not yet named; with none left, the source.
  const addCondition = () =>
    setDrafts((rows) => {
      const named = new Set(rows.filter((row) => row.field === 'tag').map((row) => row.value));
      const next = tags.find((tag) => !named.has(tag.label));
      return [
        ...rows,
        next === undefined
          ? { key: draftKey(), field: 'source', op: 'is', value: SOURCE_SUPPLIED }
          : { key: draftKey(), field: 'tag', op: 'is', value: next.label },
      ];
    });

  const canSave = name.trim().length > 0 && (!editable || draftsComplete(drafts)) && !saving;

  const refreshSegments = () =>
    queryClient.invalidateQueries({ queryKey: crmKeys.segments(config.orgRef) });

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (saved === null) {
        const created = await createCrmSegment(config.orgRef, {
          name: name.trim(),
          definition: definitionFromDrafts(drafts),
        });
        toast.success('Segment saved');
        await refreshSegments();
        const id =
          typeof created === 'object' && created !== null && 'id' in created
            ? (created as { id: unknown }).id
            : null;
        const segmentLink = config.links.segment;
        if (typeof id === 'string' && segmentLink !== undefined) {
          void navigate({ to: segmentLink(id) });
        } else if (config.links.segments !== undefined) {
          void navigate({ to: config.links.segments });
        }
      } else {
        await updateCrmSegment(
          config.orgRef,
          saved.segment.id,
          editable
            ? { name: name.trim(), definition: definitionFromDrafts(drafts) }
            : { name: name.trim() },
        );
        toast.success('Segment saved');
        await refreshSegments();
      }
    } catch (error) {
      setFormError(serverMessage(error, 'We couldn’t save this segment. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (saved === null) return;
    try {
      await deleteCrmSegment(config.orgRef, saved.segment.id);
      toast.success('Segment deleted');
      setDeleteOpen(false);
      await refreshSegments();
      if (config.links.segments !== undefined) void navigate({ to: config.links.segments });
    } catch (error) {
      toast.error(serverMessage(error, 'We couldn’t delete this segment. Please try again.'));
    }
  };

  return (
    <PageFrame>
      <PageHeader
        title={saved === null ? 'New segment' : saved.segment.name}
        actions={
          <div className="flex items-center gap-8">
            {saved === null ? null : (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => setDeleteOpen(true)}
                testId={`${PREFIX}-delete`}
              >
                Delete
              </Button>
            )}
            <Button
              appearance="saas"
              tone="primary"
              disabled={!canSave}
              loading={saving}
              onClick={() => void save()}
              testId={`${PREFIX}-save`}
            >
              Save segment
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="flex flex-col gap-24 p-24">
          <Field
            label="Name"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            testId={`${PREFIX}-name`}
          />
          <div className="flex flex-col gap-12">
            <p className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
              People who match all of these
            </p>
            {editable ? (
              <>
                {drafts.map((draft) => (
                  <div
                    key={draft.key}
                    className="flex flex-wrap items-center gap-8"
                    {...testIdProp(`${PREFIX}-condition`)}
                  >
                    <Select
                      appearance="saas"
                      aria-label="Field"
                      value={draft.field}
                      options={fieldOptions(config, draft.field)}
                      onValueChange={(value) => {
                        const field = (value ?? 'tag') as ConditionField;
                        // Choosing the row's own field again changes nothing: its value stays.
                        if (field === draft.field) return;
                        // From one date field to the other, the dates carry over.
                        if (
                          isDateField(field) &&
                          isDateField(draft.field) &&
                          draft.op !== 'within'
                        ) {
                          update(draft.key, { field });
                          return;
                        }
                        update(draft.key, {
                          field,
                          op: isDateField(field) ? 'after' : 'is',
                          value: firstValue(field),
                          end: '',
                          unit: undefined,
                        });
                      }}
                      className={
                        draft.field === 'imported_last_gift_date' ? 'w-[220px]' : 'w-[140px]'
                      }
                    />
                    <Select
                      appearance="saas"
                      aria-label="Comparison"
                      value={draft.op}
                      options={opOptions(draft.field)}
                      onValueChange={(value) => {
                        const op = (value ?? 'is') as ConditionOp;
                        if (op === draft.op) return;
                        if (op === 'within') {
                          // A window opens on two years, the year-end appeal's usual reach.
                          update(draft.key, { op, value: '2', unit: 'years', end: '' });
                        } else if (draft.op === 'within') {
                          update(draft.key, { op, value: '', end: '', unit: undefined });
                        } else {
                          update(draft.key, op === 'between' ? { op } : { op, end: '' });
                        }
                      }}
                      className="w-[160px]"
                    />
                    {draft.op === 'within' ? (
                      <>
                        <Field
                          aria-label="Number"
                          inputMode="numeric"
                          value={draft.value}
                          onChange={(event) =>
                            update(draft.key, { value: event.target.value.trim() })
                          }
                          containerClassName="w-[72px]"
                          testId={`${PREFIX}-within-amount`}
                        />
                        <Select
                          appearance="saas"
                          aria-label="Unit"
                          value={draft.unit ?? 'years'}
                          options={WINDOW_UNITS}
                          onValueChange={(value) =>
                            update(draft.key, {
                              unit: (value ?? 'years') as ConditionDraft['unit'],
                            })
                          }
                          className="w-[100px]"
                          testId={`${PREFIX}-within-unit`}
                        />
                      </>
                    ) : isDateField(draft.field) ? (
                      <DatePicker
                        appearance="saas"
                        aria-label="Date"
                        // Null, never undefined, while the row has no date: the picker stays
                        // controlled, so it shows what the row holds and never a stale day.
                        value={calendarDay(draft.value) ?? null}
                        placeholder="Choose a date"
                        onValueChange={(day) =>
                          update(draft.key, { value: day ? calendarDayString(day) : '' })
                        }
                        monthYearRange={lastGiftYears()}
                        className="w-[180px]"
                        testId={`${PREFIX}-date`}
                      />
                    ) : (
                      <Select
                        appearance="saas"
                        aria-label="Value"
                        value={draft.value.length > 0 ? draft.value : null}
                        options={valueOptions(draft)}
                        placeholder={draft.field === 'tag' ? 'No tags yet' : 'Choose…'}
                        disabled={valueOptions(draft).length === 0}
                        onValueChange={(value) => update(draft.key, { value: value ?? '' })}
                        className="w-[180px]"
                      />
                    )}
                    <Button
                      appearance="saas"
                      tone="ghost"
                      size="sm"
                      aria-label="Remove this condition"
                      onClick={() =>
                        setDrafts((rows) => rows.filter((row) => row.key !== draft.key))
                      }
                      testId={`${PREFIX}-remove-condition`}
                    >
                      <Icon icon={X} size={16} aria-hidden />
                    </Button>
                    {isDateField(draft.field) && draft.op === 'between' ? (
                      // The second date on a line of its own, under the first; "and" ends where
                      // the field and comparison columns end (140 + 8 + 160).
                      <div className="flex basis-full items-center gap-8">
                        <span className="shrink-0 text-right font-sans text-s14 tracking-givmo text-text-secondary sm:w-[308px]">
                          and
                        </span>
                        <DatePicker
                          appearance="saas"
                          aria-label="End date"
                          value={calendarDay(draft.end) ?? null}
                          placeholder="Choose a date"
                          onValueChange={(day) =>
                            update(draft.key, { end: day ? calendarDayString(day) : '' })
                          }
                          monthYearRange={lastGiftYears()}
                          className="w-[180px]"
                          testId={`${PREFIX}-date-end`}
                        />
                      </div>
                    ) : null}
                  </div>
                ))}
                {drafts.some((draft) => draft.field === 'last_gift_date') ? (
                  <p
                    className="font-sans text-s13 leading-h15 tracking-givmo text-text-tertiary"
                    {...testIdProp(`${PREFIX}-last-gift-note`)}
                  >
                    {lastGiftFieldLabel(config)} counts the gifts {config.vocab.feedSourceLabel}{' '}
                    contacts shared with you and the dates from your imports.
                  </p>
                ) : null}
                {drafts.some((draft) => draft.field === 'imported_last_gift_date') ? (
                  <p
                    className="font-sans text-s13 leading-h15 tracking-givmo text-text-tertiary"
                    {...testIdProp(`${PREFIX}-imported-date-note`)}
                  >
                    {importedLastGiftFieldLabel(config)} uses the dates from your imports, so{' '}
                    {config.vocab.feedSourceLabel} contacts never match it.
                  </p>
                ) : null}
                <div>
                  <Button
                    appearance="saas"
                    tone="link"
                    size="sm"
                    leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                    onClick={addCondition}
                    testId={`${PREFIX}-add-condition`}
                  >
                    Add a condition
                  </Button>
                </div>
              </>
            ) : (
              <p
                className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-kept`)}
              >
                This segment uses conditions this screen can’t change yet. You can rename it.
              </p>
            )}
          </div>
          {formError === null ? null : (
            <div {...testIdProp(`${PREFIX}-error`)}>
              <ErrorMessage>{formError}</ErrorMessage>
            </div>
          )}
        </Card>

        <Card className="flex flex-col" {...testIdProp(`${PREFIX}-count-card`)}>
          <div className="flex flex-col gap-4 px-20 pt-20 pb-16">
            <p className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              Right now
            </p>
            {editable && evaluation.isError ? (
              <p className="font-sans text-s14 tracking-givmo text-text-secondary">
                {isSliceUnavailable(evaluation.error)
                  ? 'Not available yet.'
                  : serverMessage(evaluation.error, 'We couldn’t count this right now.')}
              </p>
            ) : (
              <p
                className="font-sans text-s32 font-w600 tracking-givmo text-text-strong tabular-nums"
                {...testIdProp(`${PREFIX}-count`)}
              >
                {count === undefined || count === null ? '—' : displayGroupedCount(count)}{' '}
                <span className="text-s16 font-w400 text-text-secondary">
                  {count === 1 ? 'person' : 'people'}
                </span>
              </p>
            )}
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              The count updates itself as contacts change.
            </p>
            {closedMessage ? (
              <p
                className="mt-4 font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-consent-note`)}
              >
                {closedMessage}
              </p>
            ) : null}
          </div>
          {sample.length > 0 ? (
            <ul
              className="divide-y divide-border-subtle border-t border-border-subtle"
              {...testIdProp(`${PREFIX}-sample`)}
            >
              {sample.map((member) => {
                const lastGift = contactLastGift(member);
                return (
                  <li
                    key={member.contact_id}
                    className="flex items-center justify-between gap-12 px-20 py-12"
                  >
                    <span className="truncate font-sans text-s14 font-w400 tracking-givmo text-text-strong">
                      {displayText(member.name ?? member.email)}
                    </span>
                    {lastGift ? (
                      <span className="shrink-0 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary tabular-nums">
                        {displayDate(lastGift)}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : count === 0 ? (
            <p className="border-t border-border-subtle px-20 py-12 font-sans text-s13 tracking-givmo text-text-secondary">
              No one matches these yet.
            </p>
          ) : null}
          {typeof count === 'number' && count > 0 ? (
            <div className="border-t border-border-subtle px-20 py-12">
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                onClick={() => setMembersOpen(true)}
                testId={`${PREFIX}-see-all`}
              >
                See all {displayGroupedCount(count)}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <MembersDialog
        config={config}
        open={membersOpen}
        onOpenChange={setMembersOpen}
        definition={editable ? definition : null}
        savedId={editable ? null : (saved?.segment.id ?? null)}
      />

      <Dialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
        <Dialog.Content
          aria-label="Delete segment"
          className="flex w-full max-w-[440px] flex-col gap-16"
          testId={`${PREFIX}-delete-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Delete this segment?
          </Dialog.Title>
          <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
            The contacts in it stay in your CRM.
          </p>
          <div className="mt-8 flex items-center justify-end gap-8">
            <Dialog.Close
              render={<Button appearance="saas" tone="secondary" size="md" type="button" />}
            >
              Cancel
            </Dialog.Close>
            <Button
              appearance="saas"
              tone="danger"
              size="md"
              onClick={() => void remove()}
              testId={`${PREFIX}-delete-confirm`}
            >
              Delete
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </PageFrame>
  );
}

/** "See all": every member by name, ten at a time — of the conditions on screen, or a kept segment. */
function MembersDialog({
  config,
  open,
  onOpenChange,
  definition,
  savedId,
}: {
  config: CrmModuleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: CrmSegmentDefinition | null;
  savedId: string | null;
}) {
  const [offset, setOffset] = useState(0);
  const page = { limit: MEMBERS_PAGE_SIZE, offset };
  const evaluated = useQuery({
    ...crmSegmentEvaluationOptions(config.orgRef, definition ?? { v: 1, all: [] }, page),
    enabled: open && definition !== null,
  });
  const kept = useQuery({
    ...crmSegmentMembersOptions(config.orgRef, savedId ?? '', page),
    enabled: open && definition === null && savedId !== null,
  });
  const read = definition !== null ? evaluated : kept;
  const members: CrmContactOut[] = read.data?.members ?? [];
  const total = read.data?.total ?? 0;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setOffset(0);
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label="Everyone in this segment"
        width={760}
        className="flex flex-col gap-16"
        testId={`${PREFIX}-members`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {displayGroupedCount(total)} {total === 1 ? 'person' : 'people'}
        </Dialog.Title>
        {read.isPending ? (
          <Skeleton className="h-12 w-2/3" />
        ) : read.isError ? (
          <ErrorMessage>
            {serverMessage(read.error, 'We couldn’t load them. Please try again.')}
          </ErrorMessage>
        ) : (
          <ContactsTable
            rows={members.map((member) => ({
              key: member.contact_id,
              testId: `${PREFIX}-member-${member.contact_id}`,
              name: <span className="font-w600 text-text-strong">{displayText(member.name)}</span>,
              email: member.email,
              doNotEmail: false,
              tags: member.tags ?? [],
              lastGift: contactLastGift(member),
              source:
                member.source === 'feed'
                  ? config.vocab.feedSourceLabel
                  : config.vocab.suppliedSourceLabel,
            }))}
            lastGiftLabel={config.vocab.lastActivityLabel}
            showSource
            listTestId={`${PREFIX}-members-list`}
            footer={
              total > MEMBERS_PAGE_SIZE ? (
                <ContactsPagination
                  offset={offset}
                  shown={members.length}
                  total={total}
                  busy={read.isFetching}
                  onPrevious={() => setOffset((value) => Math.max(0, value - MEMBERS_PAGE_SIZE))}
                  onNext={() => setOffset((value) => value + MEMBERS_PAGE_SIZE)}
                  testId={`${PREFIX}-members-pagination`}
                />
              ) : undefined
            }
          />
        )}
        <div className="flex justify-end">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" type="button" />}
          >
            Close
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
